import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { Users, Building2, Activity, Cog } from "lucide-react";
import { Link } from "react-router-dom";

function KPI({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5B5B62]">{label}</div>
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
      </div>
      <div className="mt-3 font-display text-3xl text-[#1F2937] tracking-tight">{value}</div>
    </div>
  );
}

export default function AdminCenter() {
  const { lang } = useI18n();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [modules, setModules] = useState([]);
  const [cockpit, setCockpit] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [u, o, m, c] = await Promise.all([
          api.get("/admin/users").catch(() => ({ data: { items: [] } })),
          api.get("/admin/organisations").catch(() => ({ data: { items: [] } })),
          api.get("/admin/modules").catch(() => ({ data: { items: [] } })),
          api.get("/finance/cockpit").catch(() => ({ data: {} })),
        ]);
        setUsers(u.data.items || u.data || []);
        setOrgs(o.data.items || o.data || []);
        setModules(m.data.items || m.data || []);
        setCockpit(c.data || {});
      } catch { /* silent */ }
    })();
  }, []);

  return (
    <div className="min-h-full" data-testid="admin-center-root">
      <TopBar
        title={lang === "fr" ? "Centre d'administration" : "Admin Center"}
        subtitle={lang === "fr" ? "Vue consolidée : utilisateurs, organisations, modules, revenus." : "Consolidated view: users, organisations, modules, revenue."}
      />
      <div className="px-6 md:px-10 py-8 md:py-10 max-w-7xl mx-auto space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label={lang === "fr" ? "Utilisateurs" : "Users"} value={Array.isArray(users) ? users.length : "—"} icon={Users} />
          <KPI label={lang === "fr" ? "Organisations" : "Organisations"} value={Array.isArray(orgs) ? orgs.length : "—"} icon={Building2} />
          <KPI label={lang === "fr" ? "Modules actifs" : "Active modules"} value={
            Array.isArray(modules) ? modules.filter((m) => m.status === "active").length : "—"
          } icon={Activity} />
          <KPI label={lang === "fr" ? "Revenus (mois)" : "Revenue (month)"}
               value={(cockpit?.revenue_month || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 })}
               icon={Cog} />
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6 rounded-lg border border-[#E4E1D8] bg-white">
            <div className="px-5 py-3 border-b border-[#E4E1D8] flex items-baseline justify-between">
              <div className="font-display text-base text-[#1F2937] tracking-tight">
                {lang === "fr" ? "Utilisateurs récents" : "Recent users"}
              </div>
              <Link to="/app/governance" className="text-xs font-mono uppercase tracking-widest text-[#0B2545] hover:text-[#123163]">
                {lang === "fr" ? "Gouvernance →" : "Governance →"}
              </Link>
            </div>
            <div className="divide-y divide-[#E4E1D8] max-h-96 overflow-y-auto">
              {(Array.isArray(users) ? users : []).slice(0, 10).map((u) => (
                <div key={u.user_id || u.email} data-testid="admin-user-row" className="px-5 py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-[#1F2937]">{u.name || u.email}</div>
                    <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">
                      {(u.roles || []).join(" · ") || "end_user"}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono text-[#8B8B8F]">
                    {u.email}
                  </div>
                </div>
              ))}
              {(!Array.isArray(users) || users.length === 0) && (
                <div className="px-5 py-6 text-xs font-mono text-[#8B8B8F]">
                  {lang === "fr" ? "Aucun utilisateur listé." : "No users listed."}
                </div>
              )}
            </div>
          </div>

          <div className="col-span-12 md:col-span-6 rounded-lg border border-[#E4E1D8] bg-white">
            <div className="px-5 py-3 border-b border-[#E4E1D8] font-display text-base text-[#1F2937] tracking-tight">
              {lang === "fr" ? "Feature flags" : "Feature flags"}
            </div>
            <div className="divide-y divide-[#E4E1D8] max-h-96 overflow-y-auto">
              {(Array.isArray(modules) ? modules : []).slice(0, 30).map((m) => (
                <div key={m.key} data-testid="admin-module-row" className="px-5 py-2.5 flex items-center justify-between text-xs">
                  <div>
                    <div className="text-[#1F2937] text-sm">{m[`label_${lang}`] || m.label || m.key}</div>
                    <div className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-[#8B8B8F]">{m.key}</div>
                  </div>
                  <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded ${
                    m.status === "active" ? "text-[#059669] bg-[#14291E] border border-[#2D5A40]" :
                    m.status === "beta" ? "text-[#F2C98A] bg-[#33220D] border border-[#7A521F]" :
                    "text-[#5B5B62] bg-[#EBE8DE] border border-[#E4E1D8]"
                  }`}>
                    {m.status}
                  </span>
                </div>
              ))}
              {(!Array.isArray(modules) || modules.length === 0) && (
                <div className="px-5 py-6 text-xs font-mono text-[#8B8B8F]">
                  {lang === "fr" ? "Aucun module chargé." : "No modules loaded."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
